import { settings } from '../../../settings/settings-util';
import { checkUntilSatisfiedOrExhausted } from '../../../util/promise';
import h from 'virtual-dom/h';
import watch from 'redux-watch';
import SurveyLinkBuilder from './builders/surveyLinkBuilder';
import SurveyHeaderBuilder from "./builders/surveyHeaderBuilder";
import SurveyFooterBuilder from "./builders/surveyFooterBuilder";
import SurveyBodyBuilder from "./builders/surveyBodyBuilder";
import {AnyAction, Reducer, Store} from "redux";
import {AlmeState, defaultState} from "../../../wireup/AlmeState";
import {VChild, VNode} from "virtual-dom";
import {SaveSurveyAction, SurveyCommentGivenAction, SurveyReasonGivenAction} from "./actions/actions";
import { Mutator } from '../../../types/rendering';

type StringDictionary = {[key: string]: string};
type MutatorModule = { getMutator: () => Mutator };

export interface LinkBuilder
{
    initialize: (store: Store<AlmeState>) => void;
}

export interface HeaderBuilder
{
    initialize: (store: Store<AlmeState>) => void;
    buildHeader: (headerConfig: unknown, surveyId: string) => VChild;
}

export interface BodyBuilder
{
    initialize: (store: Store<AlmeState>) => void;
    buildBody: (bodyConfig: unknown, surveyId: string) => VChild;
}

export interface FooterBuilder
{
    initialize: (store: Store<AlmeState>) => void;
    buildFooter: (footerConfig: unknown) => VChild;
}

export default class SimpleFeedbackOverlay
{
    _settings: typeof settings;
    _store: Store<AlmeState> | null = null;
    _linkBuilder: LinkBuilder;
    _headerBuilder: HeaderBuilder;
    _bodyBuilder: BodyBuilder;
    _footerBuilder: FooterBuilder;
    _focusedElementBeforeOpen: Element | null;

    constructor(passedSettings, linkRenderer, mutatorModules: MutatorModule[] = []) {
        this._settings = passedSettings ?? settings;
        const mutators = mutatorModules.reduce((mutators, module) => [...mutators, module.getMutator()], [] as Mutator[]);

        this._linkBuilder = new SurveyLinkBuilder(this._settings, linkRenderer, mutators);
        this._headerBuilder = new SurveyHeaderBuilder(this._settings, this._linkBuilder);
        this._bodyBuilder = new SurveyBodyBuilder(this._settings, mutators);
        this._footerBuilder = new SurveyFooterBuilder(this._settings, this._linkBuilder);
        this._focusedElementBeforeOpen = null;
    }

    static getDefaultSettings(): typeof settings
    {
        return settings;
    }

    public initialize(store: Store<AlmeState>): void {
        this._store = store;

        this._linkBuilder.initialize(this._store);
        this._bodyBuilder.initialize(this._store);
        this._footerBuilder.initialize(this._store);

        const stateLocation = `${this._settings.stateLocations.Survey.Base}.${this._settings.stateLocations.Survey.Visible}`;

        const visibleWatcher = watch(this._store.getState.bind(this), stateLocation);

        this._store.subscribe(visibleWatcher((newVal, oldVal) => {
            if(newVal){
                this.openSurvey();
            }
            else if(!newVal && oldVal !== undefined){
                this.closeSurvey();
            }
        }));
    }

    getReducer(): Reducer<AlmeState["Survey"]>
    {
        return (state = defaultState.Survey, action: AnyAction): AlmeState["Survey"] =>
        {
            switch (action.type)
            {
                case "SURVEY_REASON_GIVEN":
                    return {
                        ...state,
                        reason: (action as SurveyReasonGivenAction).reason,
                        selectedReasonIndex: (action as SurveyReasonGivenAction).selectedReasonIndex,
                    };
                case "SURVEY_COMMENT_GIVEN":
                    return {
                        ...state,
                        Comment: (action as SurveyCommentGivenAction).comment,
                    };
                case "SHOW_SURVEY":
                    return {
                        ...state,
                        visible: true,
                    };
                case "SAVE_SURVEY":
                    return {
                        ...state,
                        EntryId: (action as SaveSurveyAction).EntryId,
                        surveyId: (action as SaveSurveyAction).surveyId,
                        metaData: (action as SaveSurveyAction).metaData,
                        SurveyUnitLabel: (action as SaveSurveyAction).SurveyUnitLabel,
                    };
                case "HIDE_SURVEY":
                    return {
                        ...state,
                        visible: false,
                        reason: "",
                        selectedReasonIndex: "",
                    };
                default:
                    return state;
            }
        };
    }


    //#region Builder Methods

    getRenderer() {
        return (): VNode => {
            const surveyWrapClassName = this._settings.classNames.surveyWrap ?? settings.classNames.surveyWrap;
            const currentSurveyDetails = this._store?.getState()[this._settings.stateLocations.Survey.Base][this._settings.stateLocations.Survey.MetaData];
            const currentSurveyId = this._store?.getState()[this._settings.stateLocations.Survey.Base][this._settings.stateLocations.Survey.SurveyId];
            const surveyToRender = Object.entries(currentSurveyDetails).find(entry => entry[0] === currentSurveyId);
            const survey = (surveyToRender != null) ? this.buildSurvey(surveyToRender[0], surveyToRender[1]): [];

            const surveyBaseLocation = this._settings.stateLocations.Survey.Base;
            const surveyVisibleLocation = this._settings.stateLocations.Survey.Visible;

            return h("div", {
                className: surveyWrapClassName,
                style: {
                    display: this._store?.getState()[surveyBaseLocation][surveyVisibleLocation] ? 'block' : 'none',
                },
            }, [survey]);
        };
    }

    buildSurvey(surveyId: string, surveyConfig): VChild {
        const surveyClassName = this._settings.classNames.survey ?? settings.classNames.survey;
        const padClassName = this._settings.classNames.surveyPad ?? settings.classNames.surveyPad;
        const shadowClassName = this._settings.classNames.surveyShadow ?? settings.classNames.surveyShadow;
        const surveyProperties = {
           className: surveyClassName,
           attributes: this.getSurveyAria(surveyId, surveyConfig),
        };
        const children =
        [
            this._headerBuilder.buildHeader(surveyConfig.Header, surveyId),
            this._bodyBuilder.buildBody(surveyConfig.Body, surveyId),
            this._footerBuilder.buildFooter(surveyConfig.Footer),
        ];

        const survey = h("div", surveyProperties, children);
        const pad = h("div", { className: padClassName }, [survey]);

        return h("div", { className: shadowClassName, id: surveyId }, [pad]);
    }

    //#endregion

    //#region focus Methods

    async openSurvey(): Promise<void> {
        this._focusedElementBeforeOpen = document.activeElement;
        const survey = document.getElementById(this.getSurveyId());

        const focusableTextarea = survey?.querySelector('textarea:not([disabled])');
        if (focusableTextarea) {
            await checkUntilSatisfiedOrExhausted(function(){
                return survey?.parentElement?.style.display === "block";
            }, 200, 50);
            (focusableTextarea as HTMLElement).focus();
        }
        else {
            try {
                if (!survey) { return; }
                const focusableElements = Array.from(survey?.querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'));
                await checkUntilSatisfiedOrExhausted(function(){
                    return survey?.parentElement?.style.display === "block";
                }, 200, 50);
                (focusableElements[0] as HTMLElement).focus();
            }
            catch (e) {
                console.log("Add a focusable element to overlay.");
            }
        }
    }

    closeSurvey(): void {
        if (this._focusedElementBeforeOpen) { (this._focusedElementBeforeOpen as HTMLElement).focus(); }
    }

    //#endregion

    //#region Helper Methods

    getSurveyId(): string {
        const surveyBaseLocation = this._settings.stateLocations.Survey.Base;
        const surveyIdLocation = this._settings.stateLocations.Survey.SurveyId;

        return this._store?.getState()[surveyBaseLocation][surveyIdLocation];
    }

    getSurveyAria(surveyId, surveyConfig): StringDictionary {
        const props = {
            role: 'dialog',
            'aria-modal': "true",
        };

        if (surveyConfig.Header.Title) {
            return { ...props, 'aria-labelledby': `${surveyId}-title` };
        }

        return {...props, 'aria-label': 'Additional Feedback' };
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/simple-feedback-overlay/src/simple-feedback-overlay.ts